import { useEffect, useState } from "react";
import { FunnelIcon } from "@heroicons/react/24/outline";

import { FilterMatchMode } from "primereact/api";
import { Column, ColumnFilterElementTemplateOptions } from "primereact/column";
import {
	DataTable,
	DataTableExpandedRows,
	DataTableFilterMeta,
	DataTableRowEvent,
	DataTableValueArray,
} from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { TriStateCheckbox } from "primereact/tristatecheckbox";

import { isoToLocale, relativeDate, timeAgo } from "../../../app/datetimeUtils";
import { CustomSortIcon } from "../../../components/DatatableHelpers";
import { DraftStatus, WritingAction } from "../writingApiSlice";
import { DraftActionButton } from "./DraftActionButton";
import { PromptWritingPanel } from "./PromptWritingPanel";
import { PromptStatus, PromptWriting } from "./StudentWriting";

interface WritingTableProps {
	id: string;
	title: string;
	input: PromptWriting[];
	loading: boolean;
	type: "Assignments" | "Practice";
	promptId?: number;
}

export interface WritingTableData {
	promptId: number;
	promptName: string;
	genre: string;
	isAssigned: boolean;
	dueDateUtc?: string;
	dueDateDisplay: string;
	dueDateFilter: string;
	status: string;
	updateDateUtc?: string;
	updateDateDisplay?: string;
	draftsRemaining: number;
	action?: WritingAction;
	actionId?: number;
	row: PromptWriting;
}

const statusText = (promptStatus: PromptStatus, writingStatus: string, action?: WritingAction) => {
	if (promptStatus === PromptStatus.Expired) {
		return "Expired";
	}

	switch (writingStatus) {
		case "NotStarted":
			return "Not Started";
		case "Scored":
			return "Submitted";
		case "Incomplete":
			return action === WritingAction.Organize ? "Organizing" : "Writing";
		case "ScoringFailed":
			return "Not Scored";
		default:
			return writingStatus;
	}
};

const mapWritingData = (data: PromptWriting[]): WritingTableData[] => {
	return data.map((row) => {
		return {
			promptId: row.coursePrompt.prompt.promptId,
			promptName: row.coursePrompt.prompt.name,
			genre: row.coursePrompt.prompt.genre,
			isAssigned: row.coursePrompt.isAssignedToUser,
			dueDateUtc: row.coursePrompt.dueDateUtc,
			dueDateDisplay: row.coursePrompt.dueDateUtc
				? isoToLocale(row.coursePrompt.dueDateUtc)
				: "",
			dueDateFilter:
				row.coursePrompt.dueDateUtc && Date.parse(row.coursePrompt.dueDateUtc) > Date.now()
					? "Past Due"
					: row.coursePrompt.dueDateUtc
						? relativeDate(row.coursePrompt.dueDateUtc)
						: "No Due Date",
			status: statusText(
				row.status,
				row.writing?.draftStatus ?? "NotStarted",
				row.writing?.action
			),
			updateDateUtc: row.writing?.updateDateUtc,
			updateDateDisplay: row.writing?.updateDateUtc ? timeAgo(row.writing.updateDateUtc) : "",
			draftsRemaining: row.writing
				? row.coursePrompt.draftCountLimit - row.writing.draftCount
				: row.coursePrompt.draftCountLimit,
			action: row.writing?.action,
			actionId: row.writing?.actionId,
			row: row,
		};
	});
};

const dropDownFilterTemplate = (options: ColumnFilterElementTemplateOptions, filters: string[]) => {
	return (
		<Dropdown
			id={`filter-${options.field}`}
			value={options.value}
			options={filters}
			showClear
			onChange={(e: DropdownChangeEvent) => {
				return options.filterApplyCallback(e.value);
			}}
			placeholder="Filter..."
			className="p-column-filter overflow-hidden"
			pt={{
				root: {
					className: "flex justify-between",
				},
				clearIcon: {
					className: "relative block text-red-300 mt-0 right-0 top-0",
					style: {
						position: "relative",
						display: "block",
						height: "100%",
						margin: 0,
						right: "-6px",
						top: 0,
						marginTop: "13px",
						marginLeft: "4px",
					},
				},
				input: {
					className: "py-2 px-3",
				},
				item: {
					className: "py-1 px-3",
				},
				trigger: {
					className: "",
				},
			}}
		/>
	);
};

const tristateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
	return (
		<TriStateCheckbox
			value={options.value}
			onChange={(e) => options.filterApplyCallback(e.value)}
		/>
	);
};

const inputFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
	const label: string = options.filterModel.placeholder?.toString() ?? "Filter...";
	return (
		<InputText
			id={`filter-${options.field}`}
			placeholder={label}
			value={options.value}
			onChange={(e) => options.filterApplyCallback(e.currentTarget.value)}
			className="py-2"
		/>
	);
};

export function WritingTable(props: WritingTableProps) {
	const [showFilter, setShowFilter] = useState(false);
	const [filters, setFilters] = useState<DataTableFilterMeta>({
		promptName: { value: null, matchMode: FilterMatchMode.CONTAINS },
		genre: { value: null, matchMode: FilterMatchMode.EQUALS },
		dueDateFilter: { value: null, matchMode: FilterMatchMode.EQUALS },
		status: { value: null, matchMode: FilterMatchMode.EQUALS },
	});
	const [expandedRows, setExpandedRows] = useState<DataTableExpandedRows | DataTableValueArray>();

	const data = mapWritingData(props.input);
	const statuses = [...new Set(data.map((item) => item.status))];
	const genres = [...new Set(data.map((item) => item.row.coursePrompt.prompt.genre))];
	const dueDateFilters = [
		...new Set(
			data
				.sort((a, b) => ((a.dueDateUtc ?? "") > (b.dueDateUtc ?? "") ? -1 : 1))
				.filter((item) => item.dueDateFilter != "")
				.map((item) => item.dueDateFilter)
		),
	];
	const changeFilter = !props.loading && props.input;

	useEffect(() => {
		if (props.promptId) {
			setShowFilter(true);
			setExpandedRows({ [props.promptId]: true });
		}
	}, []);

	useEffect(() => {
		if (props.promptId) {
			const filteredPromptName =
				props.input.find((p) => p.coursePrompt.prompt.promptId == props.promptId)
					?.coursePrompt.prompt.name ?? null;

			setFilters({
				...filters,
				promptName: { value: filteredPromptName, matchMode: FilterMatchMode.CONTAINS },
			});
		}
	}, [changeFilter]);

	const statusFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
		return dropDownFilterTemplate(options, statuses);
	};

	const genreFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
		return dropDownFilterTemplate(options, genres);
	};

	const dueDateFilterTemplate = (options: ColumnFilterElementTemplateOptions) => {
		return dropDownFilterTemplate(options, dueDateFilters);
	};

	const onRowExpand = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);

			const tr = target.closest("tr");
			tr && tr?.classList.add("expanded");
		}
		setExpandedRows({ [event.data.promptId]: true });
	};

	const onRowCollapse = (event: any) => {
		// clicking on the row expander triggers a nested DataTableRowEvent
		const target =
			event.originalEvent.currentTarget ??
			(event.originalEvent.originalEvent.currentTarget as HTMLElement);

		if (target) {
			const table = target.closest("table");
			table &&
				[...table.getElementsByClassName("expanded")].forEach((e: HTMLElement) =>
					e.classList.remove("expanded")
				);
		}
		setExpandedRows({});
	};

	const rowExpansionTemplate = (data: WritingTableData) => {
		return <PromptWritingPanel data={data} />;
	};

	const onRowClick = (event: DataTableRowEvent) => {
		const xRows: any = expandedRows;

		if (xRows && xRows[event.data.promptId]) {
			onRowCollapse(event);
		} else {
			onRowExpand(event);
		}
	};

	const actionBodyTemplate = (data: WritingTableData) => {
		// If the row is expanded, don't show the action buttons.
		const xRows: any = expandedRows;
		if (xRows && xRows[data.promptId]) {
			return;
		}

		if (data.row.status === PromptStatus.Expired) {
			if (!data.actionId) {
				return null;
			} else {
				return (
					<div>
						<DraftActionButton
							id={`view-report-${data.promptId}`}
							action={WritingAction.ScoreReport}
							actionId={data.actionId}
						/>
					</div>
				);
			}
		} else if (
			data.actionId &&
			data.action === WritingAction.ScoreReport &&
			data.draftsRemaining > 0 &&
			data.row.coursePrompt.isEnabled &&
			data.row.writing?.draftStatus === DraftStatus.Scored
		) {
			return (
				<div className="flex flex-col space-y-2">
					<div>
						<DraftActionButton
							id={`view-report-${data.promptId}`}
							action={data.action}
							actionId={data.actionId}
							disabled={
								data.status === PromptStatus.Expired &&
								data.action != WritingAction.ScoreReport
							}
						/>
					</div>
					<div>
						<DraftActionButton
							id={`revise-essay-${data.promptId}`}
							action={WritingAction.ReviseEssay}
							actionId={data.actionId}
							disabled={data.status === PromptStatus.Expired}
						/>
					</div>
				</div>
			);
		} else if (data.actionId) {
			return (
				<div>
					<DraftActionButton
						id={`${data.action == WritingAction.ScoreReport ? "view-report" : "continue"}-${data.promptId}`}
						action={data.action}
						actionId={data.actionId}
						disabled={
							data.status === PromptStatus.Expired &&
							data.action != WritingAction.ScoreReport
						}
					/>
				</div>
			);
		} else {
			return (
				<div className="flex flex-col space-y-2">
					{(data.row.coursePrompt.allowPrewriting ||
						data.row.coursePrompt.isOrganizerRequired) && (
						<div>
							<DraftActionButton
								id={`start-organizing-${data.promptId}`}
								action={WritingAction.StartOrganizing}
								actionId={data.promptId}
								disabled={data.status === PromptStatus.Expired}
							/>
						</div>
					)}
					{!data.row.coursePrompt.isOrganizerRequired && (
						<div>
							<DraftActionButton
								id={`start-writing-${data.promptId}`}
								action={WritingAction.StartWriting}
								actionId={data.promptId}
								disabled={data.status === PromptStatus.Expired}
							/>
						</div>
					)}
				</div>
			);
		}
	};

	return (
		<section id={props.id}>
			<div className="flex justify-between pt-4">
				<h2>{props.title}</h2>
				<div
					id={`${props.type}-show-filters-button`}
					className="btn btn-neutral cursor-pointer flex items-center"
					onClick={() => setShowFilter((v) => !v)}>
					<FunnelIcon className="h-4 w-4 inline mr-2" />
					<span className="align-middle">
						{showFilter ? "Hide Filters" : "Show Filters"}
					</span>
				</div>
			</div>
			<DataTable
				id={`${props.type}-writing-table`}
				value={data}
				dataKey="promptId"
				removableSort
				expandedRows={expandedRows}
				onRowClick={onRowClick}
				//onRowToggle={(e) => setExpandedRows(e.data)} << if we want the toggler to leave rows open, uncomment this
				onRowExpand={onRowExpand}
				onRowCollapse={onRowCollapse}
				rowExpansionTemplate={rowExpansionTemplate}
				loading={props.loading}
				selectionMode="single"
				emptyMessage="No results found"
				filters={filters}
				filterDisplay={showFilter ? "row" : undefined}
				sortIcon={CustomSortIcon}
				className="tbl tbl-data tbl-datatable min-w-[40rem] cursor-default"
				style={{ marginTop: "16px" }}>
				<Column expander={true} className="w-8"></Column>
				<Column
					header="Prompt"
					field="promptName"
					sortable
					filter
					filterElement={inputFilterTemplate}
					filterPlaceholder="Search by prompt"
					showFilterMatchModes={false}
					showClearButton={false}
					showFilterMenu={false}></Column>
				{props.type === "Practice" && (
					<Column
						header="Genre"
						field="genre"
						sortable
						filter
						filterElement={genreFilterTemplate}
						showFilterMatchModes={false}
						showFilterMenu={false}></Column>
				)}
				{props.type === "Assignments" && (
					<Column
						header="Due&nbsp;Date"
						field="dueDateDisplay"
						sortable
						sortField="dueDateUtc"
						filter
						filterField="dueDateFilter"
						filterElement={dueDateFilterTemplate}
						showAddButton={false}
						showFilterMenu={false}
						showFilterOperator={false}
						showFilterMatchModes={false}
						showApplyButton={false}
						showClearButton={true}></Column>
				)}
				<Column
					header="Status"
					field="status"
					sortable
					filter
					filterElement={statusFilterTemplate}
					showFilterMatchModes={false}
					showFilterMenu={false}></Column>
				<Column
					header="Updated"
					field="updateDateDisplay"
					sortable
					sortField="updateDateUtc"></Column>
				<Column header="Action" body={actionBodyTemplate}></Column>
			</DataTable>
		</section>
	);
}
